
import React, { useEffect, useState, useRef } from 'react';
import { Block, Manifest, PublicationType } from '../../types';
import { 
    X, UploadCloud, FileText, BarChart2, Image as ImageIcon, Plus, 
    Video, File as FileIcon, AlertTriangle, Paperclip, Loader2, RefreshCw, Trophy, CheckCircle2, HelpCircle
} from 'lucide-react';
import { RichMarkdownEditor } from './RichMarkdownEditor'; 
import { getEffectiveMediaLimit } from '../../utils/manifestUtils';
import { uploadFileWithProgress } from '../../services/api';

interface BlockEditorProps {
    block: Block;
    manifest: Manifest;
    onChange: (updatedBlock: Block) => void;
    onRemove: () => void;
    publicationType: PublicationType;
    readOnly?: boolean;
}

// Internal Interface for Editor File State
interface EditorFile {
    id: string; // Unique ID for keying
    file?: File; // The raw file (only if new)
    url: string; // Preview URL (blob) or Final URL (remote)
    name: string;
    size?: number;
    type: string;
    status: 'pending' | 'uploading' | 'success' | 'error';
    progress: number;
    serverId?: string; // The ID returned from API
    abortFn?: () => void; // Function to cancel XHR
}

export const TextBlockEditor: React.FC<BlockEditorProps> = ({ block, manifest, onChange, onRemove, readOnly = false }) => {
    const maxLength = manifest.rules.text?.maxLength || 4096;
    const textContent = block.content?.text || "";
    const currentLength = textContent.length; 
    const supportsFormatting = manifest.supports.textFormatting?.some(f => ['markdown', 'html'].includes(f));

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm group hover:border-blue-300 transition-all overflow-visible relative flex flex-col h-[420px]">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex justify-between items-center rounded-t-xl flex-shrink-0">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wide">
                    <FileText className="w-3.5 h-3.5 text-blue-500" />
                    Текст
                </div>
                <div className="flex items-center gap-2">
                    {!readOnly && (
                        <button onClick={onRemove} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Удалить блок">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
            
            <div className="flex-1 overflow-hidden flex flex-col p-0">
                <RichMarkdownEditor 
                    readOnly={readOnly}
                    value={textContent}
                    onChange={(val) => onChange({ ...block, content: { ...block.content, text: val } })}
                    supportsFormatting={!!supportsFormatting}
                    placeholder="Напишите что-нибудь интересное..."
                />
            </div>
            
            <div className="px-4 py-2 bg-slate-50/50 border-t border-slate-50 flex justify-end rounded-b-xl flex-shrink-0">
                <span className={`text-[10px] font-mono ${currentLength > maxLength ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                    {currentLength} / {maxLength}
                </span>
            </div>
        </div>
    );
};

// Generic File Manager for both Media and File blocks
const FileManager = ({ 
    block, 
    onChange, 
    readOnly, 
    manifest, 
    publicationType, 
    mode, // 'media' | 'file'
    maxItems,
    allowedTypes,
    onRemove // Added onRemove prop
}: any) => {
    const [isDragging, setIsDragging] = useState(false);

    // Initialize state from block content
    // We expect block.content.files to be our EditorFile[] array now.
    // However, if coming from API or old state, it might be simpler. 
    // We normalize on render if needed, but optimally block content holds EditorFile shape.
    const files: EditorFile[] = block.content?.files || [];

    const handleFilesSelected = (selectedFiles: File[]) => {
        if (readOnly) return;

        const newEditorFiles: EditorFile[] = selectedFiles.map(f => ({
            id: Math.random().toString(36).substr(2, 9),
            file: f,
            name: f.name,
            size: f.size,
            type: f.type,
            url: URL.createObjectURL(f),
            status: 'pending',
            progress: 0
        }));

        // Update block state immediately with pending files
        const updatedFiles = [...files, ...newEditorFiles];
        onChange({ ...block, content: { ...block.content, files: updatedFiles } });

        // Trigger Uploads
        newEditorFiles.forEach(ef => startUpload(ef, updatedFiles));
    };

    const startUpload = (ef: EditorFile, currentFilesList: EditorFile[]) => {
        if (!ef.file) return;

        // Clone list to avoid stale closure issues
        let activeList = [...currentFilesList];
        
        // Find index in the *fresh* list provided
        const updateFileState = (id: string, updates: Partial<EditorFile>) => {
            const idx = activeList.findIndex(x => x.id === id);
            if (idx !== -1) {
                activeList[idx] = { ...activeList[idx], ...updates };
                // IMPORTANT: Create new array reference for React
                activeList = [...activeList]; 
                onChange({ ...block, content: { ...block.content, files: activeList } });
            }
        };

        updateFileState(ef.id, { status: 'uploading', progress: 0 });

        const { promise, abort } = uploadFileWithProgress(ef.file, (percent) => {
            updateFileState(ef.id, { progress: percent });
        });

        // Store abort function in state so we can call it later
        updateFileState(ef.id, { abortFn: abort });

        promise
            .then(res => {
                const finalId = res.result.key || res.result.fileId || res.result.publicUrl || res.result.publicLink;
                // Use public link if available for preview, or keep blob
                const finalUrl = res.result.publicLink || res.result.publicUrl || ef.url;
                updateFileState(ef.id, { 
                    status: 'success', 
                    progress: 100, 
                    serverId: finalId,
                    url: finalUrl,
                    abortFn: undefined 
                });
            })
            .catch(err => {
                if (err.message === 'Upload cancelled') return; // Handled by remove
                console.error("Upload failed", err);
                updateFileState(ef.id, { status: 'error', progress: 0, abortFn: undefined });
            });
    };

    const removeFile = (id: string) => {
        if (readOnly) return;
        const fileToDelete = files.find(f => f.id === id);
        if (fileToDelete?.abortFn) {
            fileToDelete.abortFn(); // Cancel XHR if ongoing
        }
        const newFiles = files.filter(f => f.id !== id);
        onChange({ ...block, content: { ...block.content, files: newFiles } });
    };

    const retryUpload = (id: string) => {
        const fileToRetry = files.find(f => f.id === id);
        if (fileToRetry && fileToRetry.file) {
            startUpload(fileToRetry, files);
        }
    };

    // Calculate constraints for UI
    const maxPhotos = getEffectiveMediaLimit(manifest, publicationType, 'photo');
    const maxVideos = getEffectiveMediaLimit(manifest, publicationType, 'video');
    const maxTotal = mode === 'file' ? (maxItems || 10) : Math.max(maxPhotos, maxVideos);

    const counts = files.reduce((acc, f) => {
        const type = (f.type.startsWith('video/') || f.type === 'video') ? 'video' : 'photo';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, { photo: 0, video: 0 } as Record<string, number>);

    let error = null;
    if (!readOnly && mode === 'media') {
        if (counts.photo > maxPhotos) error = `Лимит фото: ${maxPhotos}`;
        else if (counts.video > maxVideos) error = `Лимит видео: ${maxVideos}`;
    } else if (!readOnly && files.length > maxTotal) {
        error = `Лимит файлов: ${maxTotal}`;
    }

    return (
        <div 
            className={`bg-white rounded-xl border shadow-sm group transition-colors overflow-hidden ${error ? 'border-red-300' : 'border-slate-200 hover:border-blue-300'}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files?.length) handleFilesSelected(Array.from(e.dataTransfer.files));
            }}
        >
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wide">
                    {mode === 'media' ? <ImageIcon className="w-3.5 h-3.5 text-purple-500" /> : <Paperclip className="w-3.5 h-3.5 text-orange-500" />}
                    {mode === 'media' ? 'Медиа' : 'Файлы'}
                    <span className="text-slate-300">•</span> 
                    {files.length} шт.
                </div>
                
                <div className="flex items-center gap-3">
                    {!readOnly && (
                        <div className="text-[10px] text-slate-400 font-mono hidden sm:block">
                            {isDragging ? "Отпустите файлы" : "Drag & Drop"}
                        </div>
                    )}
                    {!readOnly && onRemove && (
                         <button onClick={onRemove} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Удалить блок">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className={`p-4 transition-colors ${isDragging ? 'bg-blue-50/50' : ''}`}>
                {error && (
                    <div className="mb-4 text-xs font-medium text-red-600 bg-red-50 p-2 rounded-lg flex items-center animate-in fade-in">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                {/* Grid for Media, List for Files */}
                <div className={mode === 'media' ? "grid grid-cols-4 gap-3" : "flex flex-col gap-2"}>
                    {files.map((file, i) => {
                        const isVideo = file.type.startsWith('video/') || file.type === 'video';
                        
                        // Media Card
                        if (mode === 'media') {
                            return (
                                <div key={file.id} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden border border-slate-200 group/file">
                                    {isVideo ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900 text-white">
                                            <Video className="w-6 h-6 mb-1" />
                                            <span className="text-[9px]">VIDEO</span>
                                        </div>
                                    ) : (
                                        <img src={file.url} alt="" className="w-full h-full object-cover" />
                                    )}
                                    
                                    {/* Overlay Status */}
                                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center transition-opacity opacity-0 group-hover/file:opacity-100 p-2">
                                        {file.status === 'uploading' && (
                                            <div className="w-full text-center">
                                                <div className="w-full bg-white/20 h-1.5 rounded-full mb-1 overflow-hidden">
                                                    <div className="bg-green-400 h-full transition-all duration-300" style={{ width: `${file.progress}%` }} />
                                                </div>
                                                <span className="text-[9px] text-white font-bold">{file.progress}%</span>
                                            </div>
                                        )}
                                        {!readOnly && (
                                            <button onClick={() => removeFile(file.id)} className="absolute top-1 right-1 bg-white text-red-500 p-1 rounded-full shadow-sm hover:bg-red-50">
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                        {file.status === 'error' && !readOnly && (
                                            <button onClick={() => retryUpload(file.id)} className="mt-2 px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-[9px] flex items-center">
                                                <RefreshCw className="w-3 h-3 mr-1" /> Retry
                                            </button>
                                        )}
                                    </div>

                                    {/* Persistent Status Indicators (Visible without hover) */}
                                    {file.status === 'uploading' && (
                                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-500 animate-pulse" style={{ width: `${file.progress}%` }} />
                                    )}
                                    {file.status === 'error' && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-red-500/50 pointer-events-none">
                                            <AlertTriangle className="w-6 h-6 text-white" />
                                        </div>
                                    )}
                                </div>
                            );
                        } 
                        
                        // File List Item
                        return (
                            <div key={file.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100 relative overflow-hidden group/file">
                                {file.status === 'uploading' && (
                                    <div className="absolute bottom-0 left-0 h-0.5 bg-green-500 transition-all duration-300" style={{ width: `${file.progress}%` }} />
                                )}
                                <div className="flex items-center gap-3 overflow-hidden flex-1">
                                    <div className={`w-8 h-8 rounded bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 ${file.status === 'error' ? 'text-red-500 border-red-200' : 'text-slate-400'}`}>
                                        {file.status === 'uploading' ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <FileIcon className="w-4 h-4" />}
                                    </div>
                                    <div className="truncate min-w-0 flex-1">
                                        <div className="text-xs font-bold text-slate-700 truncate">{file.name}</div>
                                        <div className="text-[9px] text-slate-400 flex items-center gap-2">
                                            {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Файл'}
                                            {file.status === 'uploading' && <span className="text-blue-500">• Загрузка {file.progress}%</span>}
                                            {file.status === 'error' && <span className="text-red-500">• Ошибка загрузки</span>}
                                        </div>
                                    </div>
                                </div>
                                {!readOnly && (
                                    <div className="flex items-center gap-2">
                                        {file.status === 'error' && (
                                            <button onClick={() => retryUpload(file.id)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Повторить">
                                                <RefreshCw className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        <button onClick={() => removeFile(file.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {!readOnly && (files.length < maxTotal) && (
                        mode === 'media' ? (
                            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors h-full aspect-square group/add">
                                <UploadCloud className="w-5 h-5 text-slate-300 group-hover/add:text-blue-500 mb-1 transition-colors" />
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider group-hover/add:text-blue-600 text-center px-1">
                                    Добавить
                                </span>
                                <input 
                                    type="file" 
                                    multiple 
                                    accept="image/*,video/*" 
                                    className="hidden" 
                                    onChange={(e) => {
                                        if (e.target.files?.length) handleFilesSelected(Array.from(e.target.files));
                                    }} 
                                />
                            </label>
                        ) : (
                            <label className="flex items-center justify-center p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors mt-2">
                                <UploadCloud className="w-5 h-5 text-slate-300 mr-2" />
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                    Загрузить файлы
                                </span>
                                <input 
                                    type="file" 
                                    multiple 
                                    className="hidden" 
                                    onChange={(e) => {
                                        if (e.target.files?.length) handleFilesSelected(Array.from(e.target.files));
                                    }} 
                                />
                            </label>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export const MediaBlockEditor: React.FC<BlockEditorProps> = (props) => <FileManager {...props} mode="media" />;
export const FileBlockEditor: React.FC<BlockEditorProps> = (props) => {
    const fileRule = props.manifest.rules.file;
    const maxDocs = fileRule?.maxItems || 10;
    return <FileManager {...props} mode="file" maxItems={maxDocs} />;
};

export const PollBlockEditor: React.FC<BlockEditorProps> = ({ block, manifest, onChange, onRemove, readOnly = false }) => {
    const maxOptions = manifest.rules.poll?.maxOptions || 10;
    const question = block.content?.question || "";
    const options: string[] = block.content?.options || ["", ""];
    
    // Quiz state
    const isQuiz = block.content?.quiz || false;
    const correctOptionId = block.content?.correctOptionId; // 0-based index
    const explanation = block.content?.explanation || "";

    const updateOption = (idx: number, val: string) => {
        if (readOnly) return;
        const newOpts = [...options];
        newOpts[idx] = val;
        onChange({ ...block, content: { ...block.content, options: newOpts } });
    };

    const toggleQuizMode = () => {
        if (readOnly) return;
        onChange({ 
            ...block, 
            content: { 
                ...block.content, 
                quiz: !isQuiz, 
                correctOptionId: !isQuiz ? 0 : undefined, // Default to 0 if turning on
                explanation: !isQuiz ? "" : undefined 
            } 
        });
    };

    const setCorrectOption = (idx: number) => {
        if (readOnly || !isQuiz) return;
        onChange({ ...block, content: { ...block.content, correctOptionId: idx } });
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm group hover:border-green-300 transition-colors overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wide">
                    <BarChart2 className="w-3.5 h-3.5 text-green-500" />
                    Опрос
                </div>
                <div className="flex items-center gap-3">
                    {!readOnly && (
                        <button 
                            onClick={toggleQuizMode}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${isQuiz ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}
                        >
                            <Trophy className="w-3 h-3" />
                            Викторина: {isQuiz ? 'ВКЛ' : 'ВЫКЛ'}
                        </button>
                    )}
                    {!readOnly && (
                        <button onClick={onRemove} className="text-slate-300 hover:text-red-500 transition-colors p-1">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-4">
                <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase">Вопрос</label>
                    <input 
                        type="text" 
                        value={question}
                        disabled={readOnly}
                        onChange={(e) => onChange({ ...block, content: { ...block.content, question: e.target.value } })}
                        placeholder="О чем хотите спросить?"
                        className={`w-full px-3 py-2 ${readOnly ? 'bg-slate-100' : 'bg-slate-50'} border border-slate-200 rounded-lg text-sm outline-none focus:border-green-400 focus:bg-white transition-all font-medium text-slate-700`}
                    />
                </div>
                
                <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase">Варианты ответов</label>
                    <div className="space-y-2">
                        {options.map((opt, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="w-5 flex flex-col items-center justify-center">
                                    {isQuiz ? (
                                        <button 
                                            onClick={() => setCorrectOption(i)}
                                            disabled={readOnly}
                                            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${correctOptionId === i ? 'bg-green-500 border-green-500' : 'bg-white border-slate-300 hover:border-green-400'}`}
                                            title="Правильный ответ"
                                        >
                                            {correctOptionId === i && <CheckCircle2 className="w-3 h-3 text-white" />}
                                        </button>
                                    ) : (
                                        <div className="text-xs text-slate-300 font-bold">{i+1}</div>
                                    )}
                                </div>
                                <input 
                                    type="text"
                                    value={opt}
                                    disabled={readOnly}
                                    onChange={(e) => updateOption(i, e.target.value)}
                                    placeholder={`Вариант ${isQuiz && correctOptionId === i ? '(Правильный)' : ''}`}
                                    className={`flex-1 px-3 py-2 ${readOnly ? 'bg-slate-50' : 'bg-white'} border rounded-lg text-sm outline-none transition-all ${isQuiz && correctOptionId === i ? 'border-green-400 bg-green-50/20' : 'border-slate-200 focus:border-green-400'}`}
                                />
                            </div>
                        ))}
                    </div>
                    {!readOnly && options.length < maxOptions && (
                        <div className="pl-7 mt-2">
                            <button 
                                onClick={() => onChange({ ...block, content: { ...block.content, options: [...options, ""] } })}
                                className="text-xs text-green-600 font-bold hover:underline flex items-center"
                            >
                                <Plus className="w-3 h-3 mr-1" /> Добавить вариант
                            </button>
                        </div>
                    )}
                </div>

                {isQuiz && (
                    <div className="animate-in fade-in slide-in-from-top-2 pt-2 border-t border-slate-100">
                        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 mb-1.5 uppercase">
                            <HelpCircle className="w-3.5 h-3.5 text-amber-500" />
                            Пояснение (после ответа)
                        </label>
                        <textarea 
                            value={explanation}
                            disabled={readOnly}
                            onChange={(e) => onChange({ ...block, content: { ...block.content, explanation: e.target.value } })}
                            placeholder="Объясните, почему этот вариант верный (показывается пользователю после выбора)..."
                            className={`w-full px-3 py-2 ${readOnly ? 'bg-slate-100' : 'bg-amber-50/30'} border border-amber-100 rounded-lg text-sm outline-none focus:border-amber-300 focus:bg-white transition-all font-medium text-slate-700 min-h-[60px] resize-none`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};